import { Box } from '@factorialco/deprecated-design-system'
import { PayrollLegalEntitiesDocument } from 'generated/resources/graphql'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import taxonomies from 'modules/contracts/store/collections/taxonomies'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { useConcepts } from 'modules/payroll_bundle/hooks/useConcepts'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { buildPayrollConceptsByLegalEntitySettings } from 'modules/payroll_bundle/lib/settingsPayrollConcept'
import EmployeesData from 'modules/payroll_integrations/components/Content/EmployeeData'
import Table from 'modules/payroll_integrations/components/Table'
import PayrollIntegration from 'modules/payroll_integrations/lib/catalog'
import type { Codeable, ColumnNames } from 'modules/payroll_integrations/lib/types'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'

import PayrollPaywallGuard from '../../PayrollPaywallGuard'

const SilaeIntegration = () => {
  const { company } = useCurrentSession()
  const INTEGRATION_NAME = PayrollIntegration.silae

  const { hasCompensations, loading, error } = useHasCompensations()

  const { concepts: payrollConcepts, loading: loadingConcepts } = useConcepts(
    undefined,
    [
      'deductions',
      'earnings_benefits_in_kind',
      'earnings_fixed_salary',
      'earnings_others',
      'earnings_variable',
    ],
    true,
    !hasCompensations
  )

  const { data: companyInstallationsData, loading: companyInstallationsDataLoading } =
    useGetMarketplaceCompanyInstallations(
      { integrationDatoIds: [integrationCodes.silae] },
      !hasCompensations
    )
  const silaeInstallation = companyInstallationsData[0]

  const { data: legalEntitiesData, loading: legalEntitiesDataLoading } = useQuery(
    PayrollLegalEntitiesDocument,
    { variables: { companyIds: [company.id] }, skip: !hasCompensations || !company.id }
  )
  const legalEntities = legalEntitiesData?.companies?.legalEntitiesConnection?.nodes || []

  const payrollConceptsByLegalEntity = buildPayrollConceptsByLegalEntitySettings(
    payrollConcepts,
    legalEntities
  )

  const codeables: Record<string, Codeable> = {
    leave_type: {
      position: 2,
      collection: leaveTypes.filter((lt) => lt.isActive),
      class: 'Timeoff::LeaveType',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_silae.settings.form.concept.leave_type'
      ),
    },
    company: {
      position: 0,
      collection: [companies.current],
      class: 'Company',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_silae.settings.form.concept.company'
      ),
    },
  }

  if (!hasCompensations) {
    codeables.taxonomy = {
      position: 1,
      collection: taxonomies.actives,
      class: 'Contracts::Compensations::Taxonomy',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_silae.settings.form.concept.taxonomy'
      ),
    }
  }

  if (hasCompensations) {
    codeables.payrollConcepts = {
      position: 1,
      collection: payrollConceptsByLegalEntity || [],
      class: 'Payroll::Concept',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.payroll_concept'
      ),
    }
  }

  const COLUMN_NAMES: ColumnNames = [
    {
      name: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_silae.settings.form.header.concept'
      ),
      colSpan: 4,
      attribute: 'concept',
    },
    {
      name: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_silae.settings.form.header.value'
      ),
      colSpan: 4,
      attribute: 'value',
    },
  ]

  const { loading: loadingCollections } = useAsyncData(() => {
    return Promise.all([taxonomies.fetch(), leaveTypes.fetch()])
  }, [])

  const isLoading =
    loading ||
    loadingCollections ||
    legalEntitiesDataLoading ||
    companyInstallationsDataLoading ||
    loadingConcepts

  return (
    <PayrollPaywallGuard>
      <ResponsivePage.Body
        variant="full"
        loading={isLoading}
        error={error}
        banner={{
          message: i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_silae.general.codes_notice'
          ),
          color: 'warning',
        }}
      >
        <Box width="full" alignItems="center" gap="s32">
          <Table
            columnNames={COLUMN_NAMES}
            codeables={codeables}
            integration={INTEGRATION_NAME}
            integrationInstallation={silaeInstallation}
          />
        </Box>
        <EmployeesData
          integration_name={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_silae.integration_name'
          )}
        />
      </ResponsivePage.Body>
    </PayrollPaywallGuard>
  )
}

export default SilaeIntegration
